import { useEffect, useState } from "react";
import { Loader2, UserCircle } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api from "@/lib/api";
import ProfilePhotoInput from "@/components/site/ProfilePhotoInput";

const MyProfileDialog = ({ onUpdated }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/me/profile");
      setProfile(data);
    } catch {
      toast.error("Could not load your profile.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) load(); }, [open]);

  const set = (k, v) => setProfile((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch("/me/profile", {
        name: profile.name, phone: profile.phone || "",
        address: profile.address || "", photo: profile.photo || "",
      });
      localStorage.setItem("la_admin_name", data.name || data.email);
      toast.success("Profile updated.");
      setOpen(false);
      onUpdated?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save profile.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="my-profile-btn" className="rounded-full">
          <UserCircle className="h-4 w-4 sm:mr-2" /> <span className="hidden sm:inline">My Profile</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg rounded-4xl max-h-[90vh] overflow-y-auto" data-testid="my-profile-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-lg">My Profile</DialogTitle>
          <DialogDescription>Update your personal details and photo.</DialogDescription>
        </DialogHeader>
        {loading || !profile ? (
          <div className="py-10 text-center text-slate-500"><Loader2 className="h-5 w-5 mx-auto animate-spin" /></div>
        ) : (
          <form onSubmit={submit} className="space-y-4 pt-1">
            <ProfilePhotoInput value={profile.photo} onChange={(v) => set("photo", v)} fallback={(profile.name || "?").charAt(0).toUpperCase()} testId="my-photo" />
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <span className="font-semibold text-slate-700">{profile.email}</span>
              <Badge className="bg-slate-100 text-slate-600 border-0 rounded-full capitalize">{profile.role}</Badge>
              {profile.designation && <Badge className="bg-teal-50 text-ark-tealDeep border-0 rounded-full">{profile.designation}</Badge>}
            </div>
            <div className="space-y-2">
              <Label className="font-semibold text-slate-700">Name</Label>
              <Input data-testid="my-name-input" value={profile.name || ""} onChange={(e) => set("name", e.target.value)} className="rounded-2xl h-11 border-slate-200" />
            </div>
            <div className="space-y-2">
              <Label className="font-semibold text-slate-700">Phone</Label>
              <Input data-testid="my-phone-input" value={profile.phone || ""} onChange={(e) => set("phone", e.target.value)} placeholder="+91 …" className="rounded-2xl h-11 border-slate-200" />
            </div>
            <div className="space-y-2">
              <Label className="font-semibold text-slate-700">Address</Label>
              <Textarea data-testid="my-address-input" value={profile.address || ""} onChange={(e) => set("address", e.target.value)} rows={2} className="rounded-2xl border-slate-200 resize-none" />
            </div>
            {profile.role !== "superadmin" && (
              <p className="text-xs text-slate-500">Your role/designation is managed by the Super Admin.</p>
            )}
            <DialogFooter>
              <Button type="submit" disabled={saving} data-testid="my-profile-save-btn"
                className="rounded-full bg-ark-sky hover:bg-ark-skyDeep text-white font-display font-semibold w-full sm:w-auto">
                {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</> : "Save Profile"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MyProfileDialog;
